import { Inject, Injectable, OnInit } from '@angular/core';
import { LanguageService } from './language.service';
import { HttpClient } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';
import { SettingsService } from './settings.service';
import { de } from 'intl-tel-input/i18n';
import { appPhase } from '../enums';
import { INotification } from '../interfaces';
import { DebugService } from './debug.service';
import { PIMExtrav1, PIMImagev1, PIMRatev1, PIMRoomv1, PIMv1 } from '../pim-v1-interface';
import { PIMContactDetailsv2, PIMExtrav2, PIMGiftCardProductv2, PIMHotelInfov2, PIMImagev2, PIMRatev2, PIMRoomv2, PIMv2 } from '../pim-v2-interface';


@Injectable({
  providedIn: 'root'
})
export class PIMService {

  pimData:any;

  constructor(
    private languageService: LanguageService, 
    private http: HttpClient,
    private settings: SettingsService,
    private debug: DebugService,

    @Inject('environment')
    private environment: {[key: string]: string}
  ) {

  }

  onAppInit() {

    return new Promise<boolean>((resolve , reject) => {

      let endpoint = this.settings.getEndpoint();

      if (this.settings.get('app.useOldPIMJson')) {
        // if using old pim
        this.http.get(`${endpoint}/Content/json/PIM`, {
          responseType: "json",
          headers: {
            'X-Conversation-ID': uuidv4()
          }
        })
        .subscribe({
          next: (res:any) => {
            // map old pim version data to new 
            this.pimData = this.mapPIMv1ArrayToPIMv2Array(res);

            this.debug.log('Loaded pim v1', this.pimData);
            resolve(true);
          },
          error: (error) => {
            console.log('Error getting pim: ',error)
            resolve(false);
          }
        });
      }
      else {
        // if using new pim
        this.http.get(`${endpoint}/Content/json/PIM2`, {
          responseType: "json",
          headers: {
            'X-Conversation-ID': uuidv4()
          }
        })
        .subscribe({
          next: (res:any) => {
            this.pimData = res;
            this.debug.log('Loaded pim v2', this.pimData);
            resolve(true);
          },
          error: (error) => {
            console.log('Error getting pim: ',error)
            resolve(false);
          }
        });
      }
    });    
  }

  getTermsAndConditionsHtml(hotelCode: string, product:'giftcard'|'booking'|'tableReservation'='booking'): string | null {
    let hotelData:PIMv2 = this.getDataByHotelCode(hotelCode);
    let hotelInfo:PIMHotelInfov2|undefined = hotelData.hotelInfo;
    let lang = this.languageService.getCurrentLanguage();

    if (product == 'giftcard') {
      return hotelInfo?.giftCardTerms?.find((item:any) => item.lang == lang)?.text ?? null;
    }
    else if (product == 'tableReservation') {
      return hotelInfo?.tableReservationTerms?.find((item:any) => item.lang == lang)?.text ?? null;
    }
    else {
      return hotelInfo?.termsAndConditions?.find((item:any) => item.lang == lang)?.text ?? null;
    }

  }


  getHotelInfo(hotelCode: string) {
    let hotelData:PIMv2 = this.getDataByHotelCode(hotelCode); 

    let hotelInfo:PIMHotelInfov2|undefined = hotelData.hotelInfo;


    let lang = this.languageService.getCurrentLanguage();

    if (hotelInfo) {
      let infoCopy:any = JSON.parse(JSON.stringify(hotelInfo));
      let nameObj = hotelInfo.name?.find((item:any) => item.lang == lang);

      infoCopy.name = nameObj?.text;

      let longDescObj = hotelInfo.longDescription?.find((item:any) => item.lang == lang);
      infoCopy.longDescription = longDescObj?.text;

      let shortDescriptionObj = hotelInfo?.shortDescription?.find((item:any) => item.lang == lang);
      infoCopy.shortDescription = shortDescriptionObj?.text;

      return infoCopy;
    }
    return null;
  }

  getContactDetails(hotelCode: string) {
    let hotelData:PIMv2 = this.getDataByHotelCode(hotelCode); 

    return hotelData.contactDetails ?? null;
  }

  // get pim data for gift card application
  getGiftCardData(hotelCode: string, id:string) {
    let hotelData:PIMv2 = this.getDataByHotelCode(hotelCode); 
    let giftCardProduct:PIMGiftCardProductv2|undefined = hotelData?.giftCards?.find((item:any) => item.id == id);
    let lang = this.languageService.getCurrentLanguage();

    if (!giftCardProduct) {
      this.debug.log('PIM entry not found for Gift card ID:', id);
      return null;
    }

    if (giftCardProduct) {
      // filter other languages away from the item
      //let name = (room.name as { [key: string]: any })[lang];
      let giftCardCopy:any = {};
      let nameObj = giftCardProduct.name?.find((item:any) => item.lang == lang);
      if (nameObj) {
        giftCardCopy.name = nameObj.text;
      }

      let shortDescObj = giftCardProduct.shortDescription?.find((item:any) => item.lang == lang);
      if (shortDescObj) {
        giftCardCopy.shortDescription = shortDescObj?.text;
      }

      let longDescObj = giftCardProduct.longDescription?.find((item:any) => item.lang == lang);
      if (longDescObj) {
        giftCardCopy.longDescription = longDescObj?.text;
      }

      giftCardCopy.images = giftCardProduct.images;

      return giftCardCopy;
    }
  }

  getRoomData(hotelCode: string, roomCode:string) {

    let hotelData:PIMv2 = this.getDataByHotelCode(hotelCode); 

    let room:PIMRoomv2|undefined = hotelData.rooms?.find((item:any) => item.roomCode == roomCode);
    let lang = this.languageService.getCurrentLanguage();
    

    if (room) {
      // filter other languages away from the item
      //let name = (room.name as { [key: string]: any })[lang];
      let roomCopy:any = {};
      let nameObj = room.name?.find((item:any) => item.lang == lang);
      if (nameObj) {
        roomCopy.name = nameObj.text;

        let shortDescObj = room.shortDescription?.find((item:any) => item.lang == lang);
        roomCopy.shortDescription = shortDescObj?.text;
  
        let longDescObj = room.longDescription?.find((item:any) => item.lang == lang);
        roomCopy.longDescription = longDescObj?.text;
  
        roomCopy.amenities = room.amenities;
  
        let genericRoomTexts = hotelData.hotelInfo?.genericRoomTextTitle || hotelData.hotelInfo?.genericRoomTextDescription;
        if (genericRoomTexts) {
          roomCopy.genericRoomTexts = {};
          let titleObj = hotelData.hotelInfo?.genericRoomTextTitle?.find((item:any) => item.lang == lang);
          let descObj = hotelData.hotelInfo?.genericRoomTextDescription?.find((item:any) => item.lang == lang);
          roomCopy.genericRoomTexts.title = titleObj?.text
          roomCopy.genericRoomTexts.description = descObj?.text
        }
      }
      if (!nameObj) {
        this.debug.log('PIM service, translation not found for roomCode:', roomCode, ", language:", lang);
      }

      return roomCopy;
    }
    else {
      this.debug.log('PIM entry not found for roomType:', roomCode);
    }
  }

  getRateData(hotelCode: string, rateCode:string, annotations:any = []) {
    // description: Get rate data by hotel code and rate code, with optional annotations for special rates 
    // annotations: Array of strings that can include 'NON_REFUNDABLE', 'LAST_MINUTE', etc.
    // returns: PIMRatev2 object with translated names and descriptions

    let hotelData = this.getDataByHotelCode(hotelCode); 
    
    const baseCode = rateCode.split('-')[0];
    const baseRates = hotelData.rates?.filter((item: any) =>
      item.rateCode?.startsWith(baseCode)
    ) ?? [];

    const annotationPriority = ['NON_REFUNDABLE', 'LAST_MINUTE'];

    let rate: PIMRatev2 | undefined = undefined;

    for (const annotation of annotationPriority) {
      if (annotations.includes(annotation)) {
        rate = baseRates.find(item => item.rateCode === `${baseCode}-${annotation}`);
        if (rate) break;
      }
    }

    // fallback to plain base rate
    if (!rate) {
      rate = baseRates.find(item => item.rateCode === baseCode);
    }

    let lang = this.languageService.getCurrentLanguage();


    if (rate) {
      let rateCopy:any = {};

      let nameObj = rate.name?.find((item:any) => item.lang == lang);

      if (nameObj) {
        rateCopy.rateName = nameObj.text;

        let rateDescriptionObj = rate.description?.find((item:any) => item.lang == lang);
        rateCopy.rateDescription = rateDescriptionObj?.text;
  
        let cancellationPolicyObj = rate.cancellationPolicy?.find((item:any) => item.lang == lang);
        rateCopy.cancellationPolicy = cancellationPolicyObj?.text;
      }
      if (!nameObj) {
        this.debug.log('PIM service, translation not found for rateCode:', rateCode, ", language:", lang);
      }
      return rateCopy;
    }
    else {
      this.debug.log('PIM entry not found for ratecode:', rateCode);
    }
  }

  getExtrasData(hotelCode: string, rateId: string)  {
    let hotelData = this.getDataByHotelCode(hotelCode);
    let lang = this.languageService.getCurrentLanguage();

    let extra:PIMExtrav2|undefined = hotelData.extras?.find((item:any) => item.id == rateId);
    if (extra) {
      let extraCopy:any = {};

      let nameObj = extra.name?.find((item:any) => item.lang == lang);
      if (nameObj)  {
        extraCopy.name = nameObj.text;

        let shortDescObj = extra.shortDescription?.find((item:any) => item.lang == lang);
        extraCopy.shortDesc = shortDescObj?.text;
  
        let longDescObj = extra.longDescription?.find((item:any) => item.lang == lang);
        extraCopy.longDesc = longDescObj?.text;

      }
      else {
        if (!nameObj) {
          this.debug.log('PIM service, translation not found for extra:', rateId, ", language:", lang);
        }
      }
      extraCopy.images = extra.images;
      if (extra.rules) {
        extraCopy.rules = extra.rules;
      }
      return extraCopy;
    }
    else {
      this.debug.log('PIM entry not found for extra:', rateId);
    }
  }

  getImagesForRoomCode(hotelCode: string, roomCode:string) {

    let hotelData:PIMv2 = this.getDataByHotelCode(hotelCode); 

    let room:any = hotelData.rooms?.find((item:any) => item.roomCode == roomCode);
    if (room) {
      if (room.images && Array.isArray([room.images])) {
        return room.images;
      }
    }
    return [];
  } 

  getImagesForHotelCode(hotelCode:string) {

    let hotelData = this.getDataByHotelCode(hotelCode); 
    let hotelInfo:any = hotelData.hotelInfo;

    return hotelInfo.images;
  }

  getDataByHotelCode(hotelCode:string):PIMv2 {
    return this.pimData?.find((hotelData:PIMv2) => {
      return hotelData.hotelCode == hotelCode;
    });
  }

  getBeRoomCategory(roomCode:string) {
    
    let language = this.languageService.getCurrentLanguage();
    let categories = this.settings.get("searchMask.categories");

    let found = categories?.find((cat:any) => {
      return cat.roomCodes?.includes(roomCode);
    });
    
    let returnObj;
    if (found) {

      let foundCategory = found.name.find((item:any) => item.lang == language);


      returnObj = {
        name: foundCategory.text,
        value: found.value,
      }
    }
 
    return returnObj;
  }

  mapPIMv1ArrayToPIMv2Array(pimv1Array: PIMv1[]): PIMv2[] {
    const mapImage = (img: PIMImagev1): PIMImagev2 => ({
      src: img.src,
      thumb: img.thumb,
      alts: img.alt ? [{ lang: 'en', text: img.alt }] : [],
    });

    const mapExtras = (extras: PIMExtrav1[]): PIMExtrav2[] => {
      return extras?.map(extra => ({
        id: extra.id,
        name: extra.name,
        shortDescription: extra.shortDesc,
        longDescription: extra.longDesc,
        images: extra.imageUrl
          ? [{ src: extra.imageUrl, thumb: extra.imageUrl, alts: [] }]
          : [],
        rules: extra.rules || undefined,
      }));
    };

    const mapRates = (rates: PIMRatev1[]): PIMRatev2[] => {
      return rates?.map(rate => ({
        rateCode: rate.rateCode,
        name: rate.rateName,
        description: rate.rateDescription,
        cancellationPolicy: rate.cancellationPolicy,
      }));
    };

    const mapRooms = (rooms: PIMRoomv1[]): PIMRoomv2[] => {
      return rooms?.map(room => ({
        roomCode: room.roomCode,
        amenities: room.amenities,
        name: room.name,
        longDescription: room.longDescription,
        shortDescription: room.shortDescription,
        images: room.images.map(mapImage),
      }));
    };

    return pimv1Array.map(pimv1 => {
      const hotelInfo = pimv1.hotelInfo;

      const mappedPIM: PIMv2 = {
        hotelCode: pimv1.hotelCode,
        contactDetails: hotelInfo.contactDetails,
        hotelInfo: {
          name: hotelInfo?.name,
          shortDescription: hotelInfo?.shortDescription,
          longDescription: hotelInfo?.description,
          images: hotelInfo?.images.map(mapImage),
          checkIn: hotelInfo?.checkIn,
          checkOut: hotelInfo?.checkOut,
          genericRoomTextTitle: hotelInfo?.genericRoomTexts?.title,
          genericRoomTextDescription: hotelInfo?.genericRoomTexts?.description,
        },
        rooms: mapRooms(pimv1.rooms),
        rates: mapRates(pimv1.rates),
        extras: mapExtras(pimv1.extras),
      };

      return mappedPIM;
    });
  }

}
